import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { Square } from 'chess.js'
import type { BoardTheme } from '@/constants/board-themes'
import type { BoardPiece } from '@/lib/chess'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import NeonPiece from '@/components/chess/neon-piece'

import {
    COLOR_NAMES,
    FILES,
    PIECE_NAMES,
    RANKS,
    squareToCoords,
    toSquare
} from '@/lib/chess'

interface NeonBoardProps {
    pieces: BoardPiece[]
    theme: BoardTheme
    label: string
    lastMove?: { from: Square, to: Square } | null
    checkSquare?: Square | null
    selected?: Square | null
    targets?: Square[]
    hintSquare?: Square | null
    onSquareClick?: (square: Square) => void
    players?: { white: string, black: string }
    orientation?: 'w' | 'b'
    className?: string
}

const CHECK_GLOW = '#ff3355'
const HINT_GLOW = '#b6ff3b'

const ARROW_STEPS: Record<string, [number, number]> = {
    ArrowUp: [0, -1],
    ArrowDown: [0, 1],
    ArrowLeft: [-1, 0],
    ArrowRight: [1, 0]
}

const NeonBoard: FC<NeonBoardProps> = ({
    pieces,
    theme,
    label,
    lastMove = null,
    checkSquare = null,
    selected = null,
    targets = [],
    hintSquare = null,
    onSquareClick,
    players,
    orientation = 'w',
    className
}): ReactNode => {
    const interactive = Boolean(onSquareClick)
    const [focusSquare, setFocusSquare] = useState<Square>('e2')
    const buttons = useRef(new Map<Square, HTMLButtonElement>())

    const occupied = new Map(pieces.filter(piece => !piece.captured).map(piece => [piece.square, piece]))
    const targetSet = new Set(targets)

    // With Black at the bottom, every square is mirrored on both axes.
    const flipped = orientation === 'b'

    const toDisplay = (square: Square): { file: number, row: number } => {
        const { file, row } = squareToCoords(square)
        return flipped ? { file: 7 - file, row: 7 - row } : { file, row }
    }

    const fromDisplay = (file: number, row: number): Square => {
        return flipped ? toSquare(7 - file, 7 - row) : toSquare(file, row)
    }

    // A flip jumps straight to the new view instead of sliding every piece across the board.
    const shownOrientation = useRef(orientation)
    const flipping = shownOrientation.current !== orientation

    useEffect(() => {
        shownOrientation.current = orientation
    }, [orientation])

    // The last move glows in the color of the side that played it.
    const mover = lastMove ? occupied.get(lastMove.to) : undefined
    const moveColor = mover?.color === 'b' ? theme.black : theme.white

    const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, square: Square) => {
        const step = ARROW_STEPS[event.key]
        if (!step) return

        event.preventDefault()

        const { file, row } = toDisplay(square)
        const nextFile = Math.min(7, Math.max(0, file + step[0]))
        const nextRow = Math.min(7, Math.max(0, row + step[1]))
        const next = fromDisplay(nextFile, nextRow)

        setFocusSquare(next)
        buttons.current.get(next)?.focus()
    }

    const describe = (square: Square, piece: BoardPiece | undefined, isTarget: boolean, isSelected: boolean): string => {
        const parts: string[] = [square]

        parts.push(piece ? `${COLOR_NAMES[piece.color]} ${PIECE_NAMES[piece.type]}` : 'empty')
        if (isSelected) parts.push('selected')
        if (isTarget) parts.push(piece ? 'capture available' : 'legal move')

        return parts.join(', ')
    }

    const squares = RANKS.flatMap((_rank, row) => FILES.map((_file, file) => {
        const square = fromDisplay(file, row)
        const fileLabel = square[0]
        const rankLabel = square[1]
        const isLight = (row + file) % 2 === 0
        const piece = occupied.get(square)
        const isLastMove = Boolean(lastMove && (lastMove.from === square || lastMove.to === square))
        const isSelected = selected === square
        const isTarget = targetSet.has(square)
        const isCheck = checkSquare === square
        const isHint = hintSquare === square

        const layers = (
            <>
                {isLastMove && (
                    <span
                        aria-hidden='true'
                        className='absolute inset-0 transition-colors duration-500'
                        style={{
                            backgroundColor: `${moveColor}${square === lastMove?.to ? '59' : '40'}`,
                            boxShadow: `inset 0 0 0 1.5px ${moveColor}d9, inset 0 0 16px ${moveColor}59, 0 0 18px ${moveColor}40`
                        }}
                    />
                )}

                {isCheck && (
                    <span
                        aria-hidden='true'
                        className='absolute inset-0'
                        style={{ background: `radial-gradient(circle, ${CHECK_GLOW}b3 0%, ${CHECK_GLOW}4d 42%, transparent 72%)` }}
                    />
                )}

                {isSelected && (
                    <span
                        aria-hidden='true'
                        className='absolute inset-0'
                        style={{
                            backgroundColor: `${theme.white}33`,
                            boxShadow: `inset 0 0 0 2px ${theme.white}, inset 0 0 20px ${theme.white}66`
                        }}
                    />
                )}

                {isHint && (
                    <span
                        aria-hidden='true'
                        className='absolute inset-[10%] rounded-full animate-neon-ping'
                        style={{ boxShadow: `0 0 0 3px ${HINT_GLOW}, 0 0 18px ${HINT_GLOW}` }}
                    />
                )}

                {isTarget && !piece && (
                    <span
                        aria-hidden='true'
                        className='absolute left-1/2 top-1/2 size-[26%] -translate-x-1/2 -translate-y-1/2 rounded-full'
                        style={{ backgroundColor: theme.white, boxShadow: `0 0 10px ${theme.white}, 0 0 22px ${theme.white}99` }}
                    />
                )}

                {isTarget && piece && (
                    <span
                        aria-hidden='true'
                        className='absolute inset-[6%] rounded-full'
                        style={{ boxShadow: `inset 0 0 0 3px ${theme.white}, 0 0 14px ${theme.white}aa` }}
                    />
                )}

                {file === 0 && (
                    <span
                        aria-hidden='true'
                        className='absolute left-[6%] top-[6%] font-mono text-[9px] leading-none sm:text-[11px]'
                        style={{ color: `${theme.white}8c` }}
                    >
                        {rankLabel}
                    </span>
                )}

                {row === 7 && (
                    <span
                        aria-hidden='true'
                        className='absolute bottom-[6%] right-[7%] font-mono text-[9px] leading-none sm:text-[11px]'
                        style={{ color: `${theme.white}8c` }}
                    >
                        {fileLabel}
                    </span>
                )}
            </>
        )

        const background = { backgroundColor: isLight ? theme.light : theme.dark }

        if (!interactive) {
            return (
                <div key={square} className='relative transition-colors duration-500' style={background}>
                    {layers}
                </div>
            )
        }

        return (
            <button
                key={square}
                ref={element => {
                    if (element) buttons.current.set(square, element)
                    else buttons.current.delete(square)
                }}
                type='button'
                tabIndex={square === focusSquare ? 0 : -1}
                aria-label={describe(square, piece, isTarget, isSelected)}
                onClick={() => onSquareClick?.(square)}
                onKeyDown={event => handleKeyDown(event, square)}
                onFocus={() => setFocusSquare(square)}
                className='relative cursor-pointer outline-none transition-colors duration-500 focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white'
                style={background}
            >
                {layers}
            </button>
        )
    }))

    return (
        <div className={className}>
            <div
                className='relative rounded-2xl p-px transition-shadow duration-500'
                style={{
                    background: `linear-gradient(135deg, ${theme.white}, ${theme.white}66 35%, ${theme.black}66 65%, ${theme.black})`,
                    boxShadow: `-14px 0 44px -16px ${theme.white}73, 14px 0 44px -16px ${theme.black}73, 0 28px 60px -28px rgb(0 0 0 / 0.9)`
                }}
            >
                <div className='relative aspect-square w-full overflow-hidden rounded-[15px]' style={{ backgroundColor: theme.dark }}>
                    <div
                        className='grid size-full grid-cols-8 grid-rows-8'
                        role={interactive ? 'group' : 'img'}
                        aria-label={label}
                    >
                        {squares}
                    </div>

                    <div aria-hidden='true' className='pointer-events-none absolute inset-0'>
                        {pieces.map(piece => {
                            const { file, row } = toDisplay(piece.square)
                            const color = piece.color === 'w' ? theme.white : theme.black

                            return (
                                <div
                                    key={piece.id}
                                    className={cn(
                                        'absolute left-0 top-0 flex size-[12.5%] items-center justify-center',
                                        !flipping && 'transition-[transform,opacity,color] duration-500 ease-out motion-reduce:transition-none'
                                    )}
                                    style={{
                                        transform: `translate(${file * 100}%, ${row * 100}%) scale(${piece.captured ? 0.5 : 1})`,
                                        opacity: piece.captured ? 0 : 1,
                                        color,
                                        filter: `drop-shadow(0 0 1.5px ${color}cc) drop-shadow(0 0 6px ${color}66)`
                                    }}
                                >
                                    <NeonPiece type={piece.type} className='size-[92%]' />
                                </div>
                            )
                        })}
                    </div>
                </div>
            </div>

            {players && (
                <div className='mt-3.5 flex items-center justify-between gap-4 px-0.5 font-mono text-[10px] uppercase tracking-[0.22em] sm:text-[11px]'>
                    <span className='truncate transition-colors duration-500' style={{ color: theme.white, textShadow: `0 0 10px ${theme.white}59` }}>
                        White: {players.white}
                    </span>

                    <span className='truncate text-right transition-colors duration-500' style={{ color: theme.black, textShadow: `0 0 10px ${theme.black}59` }}>
                        Black: {players.black}
                    </span>
                </div>
            )}
        </div>
    )
}

export default NeonBoard
